import type { StockTakeLine } from '../../../infrastructure/FulfillmentAdminClient';
import { keyOf, shelfLabel, skusNamedBy } from './inventoryView';
import type { InventoryScreenRow } from './inventoryView';

/**
 * A full count walk is a stock audit over the whole warehouse, shelf by shelf, counted the way the FC counts on paper:
 * every box starts blank, a product is found by scanning it, and Enter goes back to the scanner (SSD-7662). The server
 * stores any non-blank audit id, so the walk is marked by the id it opens under, as a Random check is.
 */
export const FULL_COUNT_PREFIX = 'fullcount-';

export const isFullCount = (auditGroupId: string | null | undefined): boolean =>
  typeof auditGroupId === 'string' && auditGroupId.startsWith(FULL_COUNT_PREFIX);

export const fullCountGroupId = (uuid: string): string => `${FULL_COUNT_PREFIX}${uuid}`;

/** Shelf codes in walk order: numbers compare as numbers, so A1-2 comes before A1-10, as the table sorts them. */
export const walkShelves = (codes: Iterable<string>): string[] =>
  [...new Set(codes)].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

/** The shelf one step from `current` in walk order, or null at either end. A shelf not in the walk starts from the top. */
export const stepShelf = (shelves: string[], current: string | null, step: 1 | -1): string | null => {
  if (shelves.length === 0) {
    return null;
  }
  const at = current === null ? -1 : shelves.indexOf(current);
  if (at === -1) {
    return step === 1 ? (shelves[0] ?? null) : null;
  }
  return shelves[at + step] ?? null;
};

/**
 * The rows of the current shelf a scanned code names: by sku or barcode, exactly (a part of a code names none). One code
 * can stand for more than one product, so the screen lets the counter pick when it names several.
 */
export const rowsNamedOnShelf = (code: string, shelf: string, rows: InventoryScreenRow[]): InventoryScreenRow[] => {
  const onShelf = rows.filter((row) => row.locationCode === shelf);
  const skus = new Set(skusNamedBy(code, onShelf));
  return onShelf.filter((row) => skus.has(row.sku));
};

/** What a row of the walk stands at: the verdict chip on screen and the 판정 column of the export. */
export type FullCountVerdict =
  | 'not-counted'
  | 'barcode-unchecked'
  | 'barcode-correction'
  | 'moved-out'
  | 'matches'
  | 'over'
  | 'short'
  | 'not-on-shelf'
  | 'added'
  | 'added-not-counted';

export const VERDICT_LABELS: Record<FullCountVerdict, string> = {
  'not-counted': 'Not counted',
  'barcode-unchecked': 'Barcode not checked',
  'barcode-correction': 'Barcode correction',
  'moved-out': 'Moved',
  matches: 'Count matches',
  over: 'Over',
  short: 'Short',
  'not-on-shelf': 'Not on shelf',
  added: 'Added',
  'added-not-counted': 'Added · not counted',
};

/** One row of the walk as the verdict reads it: what the book held when it was counted and what was counted. */
export interface WalkCount {
  /** The count written to the stock, or null while the row is uncounted. */
  countedQty: number | null;
  /** The book the count was made against: the count minus the gap it wrote. */
  bookAtCount: number | null;
  /** Added to the shelf during the walk: the book had no row for it there. */
  found: boolean;
  /** The counter scanned the product's 88 code, or confirmed it by name with a reason [SSD-7662]. */
  barcodeChecked: boolean;
  /** The counter suggested another barcode for the product; the row needs checking again [SSD-7662]. */
  barcodeCorrection: boolean;
  /** The row's whole stock was moved to another shelf of the walk [SSD-7662]. */
  movedOut: boolean;
}

/**
 * The verdict in the order the floor's own stock-take app reads a row [SSD-7662]: moved, not on the shelf, barcode
 * correction, added, barcode not checked, not counted, then the count against the book. A count only reads as matching
 * once the product was confirmed by its 88 code (or by name when it has none). A row counted at 0 has nothing to scan.
 */
export const verdictOf = (count: WalkCount): FullCountVerdict => {
  if (count.movedOut) {
    return 'moved-out';
  }
  if (!count.found && count.countedQty === 0 && (count.bookAtCount ?? 0) > 0) {
    return 'not-on-shelf';
  }
  if (count.barcodeCorrection) {
    return 'barcode-correction';
  }
  if (count.found) {
    return count.countedQty === null ? 'added-not-counted' : 'added';
  }
  if (count.countedQty === null || count.bookAtCount === null) {
    return 'not-counted';
  }
  if (!count.barcodeChecked && count.countedQty > 0) {
    return 'barcode-unchecked';
  }
  if (count.countedQty === count.bookAtCount) {
    return 'matches';
  }
  return count.countedQty > count.bookAtCount ? 'over' : 'short';
};

/** The applied count of a row, keyed like the screen's rows; the walk keeps one per counted row. */
export interface AppliedWalkCount {
  countedQty: number;
  delta: number;
  at: string | null;
  /** How the product was confirmed: the scanned code, or the reason it was checked by name [SSD-7662]. */
  barcodeCheck?: { method: 'SCAN' | 'NAME'; code?: string; reason?: string } | null;
  note?: string | null;
  barcodeCorrection?: string | null;
  /** The shelf the row's stock arrived from by a move. */
  movedFrom?: string | null;
  /** The shelf the row's whole stock went to by a move. */
  movedTo?: string | null;
  /** How many units that move took. */
  movedQty?: number | null;
}

/** The marks a server line carries [SSD-7662], in the shape the walk keeps beside its applied count. */
export const annotationsOfLine = (
  line: Pick<StockTakeLine, 'barcodeCheck' | 'note' | 'barcodeCorrection' | 'movedFrom' | 'movedTo'>,
): Pick<AppliedWalkCount, 'barcodeCheck' | 'note' | 'barcodeCorrection' | 'movedFrom' | 'movedTo' | 'movedQty'> => ({
  ...(line.barcodeCheck && {
    barcodeCheck: {
      method: line.barcodeCheck.method,
      ...(line.barcodeCheck.code !== undefined && { code: line.barcodeCheck.code }),
      ...(line.barcodeCheck.reason !== undefined && { reason: line.barcodeCheck.reason }),
    },
  }),
  ...(line.note?.text && { note: line.note.text }),
  ...(line.barcodeCorrection?.code && { barcodeCorrection: line.barcodeCorrection.code }),
  ...(line.movedFrom && { movedFrom: line.movedFrom.locationCode }),
  ...(line.movedTo && { movedTo: line.movedTo.locationCode, movedQty: line.movedTo.qty }),
});

/** A row's walk count from what was written for it, so the verdict never follows a number typed but not saved. */
export const walkCountOf = (
  row: InventoryScreenRow,
  applied: AppliedWalkCount | undefined,
  found: boolean,
): WalkCount => {
  const marks = {
    barcodeChecked: Boolean(applied?.barcodeCheck),
    barcodeCorrection: Boolean(applied?.barcodeCorrection?.trim()),
    movedOut: Boolean(applied?.movedTo),
  };
  return applied
    ? { countedQty: applied.countedQty, bookAtCount: applied.countedQty - applied.delta, found, ...marks }
    : { countedQty: null, bookAtCount: row.onHand, found, ...marks };
};

export interface WalkSummary {
  rows: number;
  counted: number;
  matches: number;
  changed: number;
  added: number;
  notOnShelf: number;
  /** Counted rows nobody confirmed by barcode or by name [SSD-7662]. */
  barcodeUnchecked: number;
  /** Rows carrying a barcode correction for the catalog team [SSD-7662]. */
  corrections: number;
  /** Rows whose stock was moved to another shelf of the walk [SSD-7662]. */
  moved: number;
}

/** The walk's five numbers: every row, counted, counted as the book, counted off the book, added, and not on the shelf. */
export const walkSummary = (
  rows: InventoryScreenRow[],
  applied: Record<string, AppliedWalkCount>,
  found: ReadonlySet<string>,
): WalkSummary =>
  rows.reduce<WalkSummary>(
    (sum, row) => {
      const count = walkCountOf(row, applied[keyOf(row)], found.has(keyOf(row)));
      const verdict = verdictOf(count);
      // A gap is a gap whatever chip the row wears: an unchecked barcode does not hide a short count from the tally.
      const gap =
        !count.found && !count.movedOut && count.countedQty !== null && count.countedQty !== count.bookAtCount;
      return {
        rows: sum.rows + 1,
        counted: sum.counted + (applied[keyOf(row)] ? 1 : 0),
        matches: sum.matches + (verdict === 'matches' ? 1 : 0),
        changed: sum.changed + (gap ? 1 : 0),
        added: sum.added + (count.found ? 1 : 0),
        notOnShelf: sum.notOnShelf + (verdict === 'not-on-shelf' ? 1 : 0),
        barcodeUnchecked: sum.barcodeUnchecked + (verdict === 'barcode-unchecked' ? 1 : 0),
        corrections: sum.corrections + (count.barcodeCorrection ? 1 : 0),
        moved: sum.moved + (count.movedOut ? 1 : 0),
      };
    },
    {
      rows: 0,
      counted: 0,
      matches: 0,
      changed: 0,
      added: 0,
      notOnShelf: 0,
      barcodeUnchecked: 0,
      corrections: 0,
      moved: 0,
    },
  );

/** One line of the walk's history: what was written to which row, and when. */
export interface WalkHistoryEntry {
  at: string;
  locationCode: string;
  sku: string;
  name: string | null;
  verdict: FullCountVerdict;
  countedQty: number;
  bookAtCount: number;
}

/** What the walk wrote, newest first, read from the counts themselves so a refresh or another device shows the same. */
export const walkHistory = (
  rows: InventoryScreenRow[],
  applied: Record<string, AppliedWalkCount>,
  found: ReadonlySet<string>,
): WalkHistoryEntry[] =>
  rows
    .flatMap((row) => {
      const entry = applied[keyOf(row)];
      if (!entry || !entry.at) {
        return [];
      }
      const count = walkCountOf(row, entry, found.has(keyOf(row)));
      return [
        {
          at: entry.at,
          locationCode: row.locationCode,
          sku: row.sku,
          name: row.name ?? null,
          verdict: verdictOf(count),
          countedQty: entry.countedQty,
          bookAtCount: count.bookAtCount ?? 0,
        },
      ];
    })
    .sort((a, b) => b.at.localeCompare(a.at));

/**
 * The shelf a walk picks back up at: the one with the latest count, or the first shelf when nothing is counted yet.
 * Counts are keyed like the screen's rows, `sku@shelf`.
 */
export const resumeShelf = (shelves: string[], applied: Record<string, { at: string | null }>): string | null => {
  let latest: { at: string; shelf: string } | null = null;
  for (const [key, entry] of Object.entries(applied)) {
    const shelf = key.slice(key.indexOf('@') + 1);
    if (entry.at && shelves.includes(shelf) && (latest === null || entry.at > latest.at)) {
      latest = { at: entry.at, shelf };
    }
  }
  return latest?.shelf ?? shelves[0] ?? null;
};

/** The products of the walk the catalog holds no barcode for; each is checked by name until one is registered. */
export const barcodelessCount = (rows: InventoryScreenRow[]): number =>
  new Set(rows.filter((row) => !row.barcode?.trim()).map((row) => row.sku)).size;

const csvCell = (value: string | number | null | undefined): string => {
  const text = String(value ?? '');
  // Text opening with =, +, - or @ is run as a formula by Excel and Sheets; the apostrophe defuses it. A number is a
  // number: a gap of -1 must stay -1 for the sheet to add it up.
  const safe = typeof value === 'string' && /^\s*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

/**
 * The 18 columns of the FC's own stock-take file, so the export drops into the sheet the floor already keeps. 현재바코드
 * carries a barcode correction when the counter wrote one; the source row stays blank, the walk has no such number.
 */
export const FULL_COUNT_CSV_HEADERS = [
  '행ID',
  '구분',
  '원본행',
  '로케이션',
  '원본SKU',
  '원본바코드',
  '원본상품명',
  '원본수량',
  '현재SKU',
  '현재바코드',
  '현재상품명',
  '규격',
  '실사수량',
  '차이',
  '판정',
  '비고',
  '원래로케이션',
  '위치변경',
] as const;

/** The 구분 column: how the row came into the walk. */
const kindOf = (count: WalkCount, verdict: FullCountVerdict): string =>
  count.found
    ? '추가'
    : verdict === 'moved-out'
      ? '이동'
      : verdict === 'not-on-shelf'
        ? '삭제'
        : count.barcodeCorrection ||
            (count.countedQty !== null && count.bookAtCount !== null && count.countedQty !== count.bookAtCount)
          ? '수정'
          : '기존';

/**
 * The walk as a CSV the way the FC file writes it (BOM, CRLF, every cell quoted): the whole walk, or only the rows
 * that changed (off the book, added, or not on the shelf). Null when there is nothing to write.
 */
export const fullCountCsv = (
  rows: InventoryScreenRow[],
  applied: Record<string, AppliedWalkCount>,
  found: ReadonlySet<string>,
  changesOnly: boolean,
): string | null => {
  const lines = rows.flatMap((row) => {
    const key = keyOf(row);
    const count = walkCountOf(row, applied[key], found.has(key));
    const verdict = verdictOf(count);
    const kind = kindOf(count, verdict);
    const entry = applied[key];
    if (changesOnly && kind === '기존' && verdict !== 'barcode-unchecked' && !entry?.movedFrom) {
      return [];
    }
    // Only what was written to the stock is exported: a number typed and not saved is not a count yet.
    const counted = count.countedQty;
    const diff = count.countedQty !== null ? count.countedQty - (count.bookAtCount ?? 0) : null;
    const added = count.found;
    return [
      [
        key,
        kind,
        '',
        shelfLabel(row.locationCode),
        added ? '' : row.sku,
        added ? '' : (row.barcode ?? ''),
        added ? '' : (row.name ?? ''),
        added ? '' : (count.bookAtCount ?? row.onHand),
        row.sku,
        entry?.barcodeCorrection?.trim() || row.barcode || '',
        row.name ?? '',
        row.size ?? '',
        counted ?? '',
        diff ?? '',
        VERDICT_LABELS[verdict],
        entry?.note ?? '',
        shelfLabel(entry?.movedFrom ?? row.locationCode),
        entry?.movedTo
          ? `→ ${shelfLabel(entry.movedTo)}${entry.movedQty ? ` ${entry.movedQty}개` : ''}`
          : entry?.movedFrom
            ? '이동해 옴'
            : '',
      ]
        .map(csvCell)
        .join(','),
    ];
  });
  if (lines.length === 0) {
    return null;
  }
  return `\uFEFF${[FULL_COUNT_CSV_HEADERS.map(csvCell).join(','), ...lines].join('\r\n')}`;
};
